/** Loading placeholders drawn in the shape of what is coming, not a spinner.
 *
 * `Skeleton` is one bone. `SkeletonRows` is a list or table body, `SkeletonTile` a KPI tile. The group
 * is a live region (`role="status"`, `aria-busy`) that says "Loading" to a screen reader once; the
 * bones themselves are decoration. The bones pulse in opacity only, appear after 150ms of loading, and reduced
 * motion leaves a still bone.
 */

import type { CSSProperties } from "react";

import styles from "./Skeleton.module.css";

export function Skeleton({
  width = "100%",
  height = 12,
  radius,
  style,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={styles.bone}
      aria-hidden="true"
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

function Announce({ label }: { label: string }) {
  return <span className="visuallyHidden">{label}</span>;
}

export function SkeletonRows({
  rows = 5,
  lead = false,
  label = "Loading…",
}: {
  rows?: number;
  /** A round lead bone at the start of each row (an avatar or an icon). */
  lead?: boolean;
  label?: string;
}) {
  return (
    <div className={styles.rows} role="status" aria-busy="true" data-testid="skeleton-rows">
      <Announce label={label} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={styles.row} aria-hidden="true">
          {lead ? <Skeleton width={28} height={28} radius="50%" /> : null}
          <div className={styles.lines}>
            <Skeleton width={`${70 - ((i * 9) % 30)}%`} />
            <Skeleton width={`${40 - ((i * 7) % 16)}%`} height={10} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTile({ label = "Loading…" }: { label?: string }) {
  return (
    <div className={styles.tile} role="status" aria-busy="true" data-testid="skeleton-tile">
      <Announce label={label} />
      <div aria-hidden="true" className={styles.tileBody}>
        <Skeleton width="45%" height={10} />
        <Skeleton width="62%" height={28} radius={6} />
        <Skeleton width="38%" height={10} />
      </div>
    </div>
  );
}
